import { Trans } from "@lingui/macro";
import { FC, useCallback, useEffect, useState } from "react";
import { ProductVariation, SingleProduct } from "src/types/graphql.d";
import { CheckSmall } from "src/icons/CheckSmall";
import { CloseSmall } from "src/icons/CloseSmall";
import {
  useSizeSchemaFilter,
  SizeMapping,
} from "src/hooks/useSizeSchemaFilter";

import {
  DropdownContainer,
  HeaderRow,
  ColumnHeader,
  SchemaButton,
  SchemaChevron,
  CloseIconWrapper,
  SizeList,
  SizeListWrapper,
  BrandSizeColumnSpacer,
  SizeRow,
  SizeCell,
  StockIndicator,
  NotifyMeIndicator,
  SizeSchemaOverlay,
  SizeSchemaDropdownWrapper,
  SizeSchemaDropdown,
  SizeSchemaMappingWrapper,
  SizeSchemaMapping,
  CheckIconWrapper,
} from "./SizeSelectorDropdown.styled";
import { theme } from "src/theme";
import { BackInStockNotification } from "src/molecules/BackInStockNotification";

interface SizeSelectorPanelProps {
  isOpen: boolean;
  variations: ProductVariation[];
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  locale?: string;
  getStockText?: (quantity: number) => React.ReactNode | null;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
  product?: SingleProduct;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
}

const SizeSelectorPanel: FC<SizeSelectorPanelProps> = ({
  isOpen,
  variations,
  selectedVariationId,
  onSelectVariation,
  locale,
  getStockText,
  selectedSchema: externalSchema,
  onSchemaChange,
  product,
  onBackInStockPopupChange,
}) => {
  const [isSchemaDropdownOpen, setIsSchemaDropdownOpen] = useState(false);
  const [notifyPopupSizeMapping, setNotifyPopupSizeMapping] =
    useState<SizeMapping | null>(null);

  const {
    availableSchemaMappings,
    selectedSizeSchemaMapping,
    sizeMappings,
    hasMultipleSchemas,
    selectedSchema,
    handleSelectSchema: onSchemaSelect,
    formatBrandSize,
    formatTranslatedSize,
  } = useSizeSchemaFilter({
    variations,
    locale,
    selectedSchema: externalSchema,
    onSchemaChange,
  });

  useEffect(() => {
    if (!isOpen) {
      setIsSchemaDropdownOpen(false);
    }
  }, [isOpen]);

  const handleToggleSchemaDropdown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSchemaDropdownOpen((prev) => !prev);
  }, []);

  const handleSelectSchema = useCallback(
    (schemaKey: string) => {
      onSchemaSelect(schemaKey);
      setIsSchemaDropdownOpen(false);
    },
    [onSchemaSelect]
  );

  return (
    <>
      <DropdownContainer isOpen={isOpen}>
        <HeaderRow>
          <ColumnHeader isOpen={isSchemaDropdownOpen}>
            <Trans>Brand size</Trans>
          </ColumnHeader>
          <ColumnHeader
            key={selectedSchema}
            isClickable={hasMultipleSchemas}
            isOpen={isSchemaDropdownOpen}
          >
            <SchemaButton
              type="button"
              isOpen={isSchemaDropdownOpen}
              onClick={
                hasMultipleSchemas ? handleToggleSchemaDropdown : undefined
              }
              aria-expanded={isSchemaDropdownOpen}
              style={{ cursor: hasMultipleSchemas ? "pointer" : "default" }}
            >
              <span>{selectedSizeSchemaMapping?.title}</span>
              {hasMultipleSchemas &&
                (isSchemaDropdownOpen ? (
                  <CloseIconWrapper>
                    <CloseSmall
                      width="9px"
                      height="9px"
                      fill={theme.colors.tone.black}
                    />
                  </CloseIconWrapper>
                ) : (
                  <SchemaChevron />
                ))}
            </SchemaButton>
          </ColumnHeader>
        </HeaderRow>

        <SizeListWrapper>
          <SizeList role="listbox" key={selectedSchema}>
            {sizeMappings.map((sizeMap) => {
              const isSelected = sizeMap.variationId === selectedVariationId;
              const brandSizeFormatted = formatBrandSize(sizeMap);
              const translatedSizeFormatted = formatTranslatedSize(
                sizeMap,
                false
              );
              const stockText = getStockText?.(sizeMap.stockQuantity);

              const isOutOfStock = sizeMap.stockQuantity === 0;

              const handleRowClick = () => {
                if (isOutOfStock) {
                  setNotifyPopupSizeMapping(sizeMap);
                  onBackInStockPopupChange?.(true);
                } else {
                  onSelectVariation(sizeMap.variationId);
                }
              };

              return (
                <SizeRow
                  key={sizeMap.variationId}
                  type="button"
                  isSelected={isSelected}
                  isOutOfStock={isOutOfStock}
                  onClick={handleRowClick}
                  role="option"
                  aria-selected={isSelected}
                >
                  <SizeCell
                    $isSelected={isSelected}
                    $isOutOfStock={isOutOfStock}
                  >
                    {brandSizeFormatted}
                  </SizeCell>
                  <SizeCell
                    $isSelected={isSelected}
                    $isOutOfStock={isOutOfStock}
                  >
                    {translatedSizeFormatted}
                    {isOutOfStock ? (
                      <NotifyMeIndicator>
                        <Trans>Notify me</Trans>
                      </NotifyMeIndicator>
                    ) : (
                      stockText && (
                        <StockIndicator $isSelected={isSelected}>
                          {stockText}
                        </StockIndicator>
                      )
                    )}
                  </SizeCell>
                </SizeRow>
              );
            })}
          </SizeList>
        </SizeListWrapper>
        <BrandSizeColumnSpacer $isVisible={isSchemaDropdownOpen} />
        <SizeSchemaOverlay
          isVisible={isSchemaDropdownOpen}
          onClick={() => setIsSchemaDropdownOpen(false)}
        />
        <SizeSchemaDropdownWrapper>
          <SizeSchemaDropdown isOpen={isSchemaDropdownOpen}>
            <SizeSchemaMappingWrapper>
              {availableSchemaMappings.map((schemaMap) => {
                const isSelected = schemaMap.key === selectedSchema;
                return (
                  <SizeSchemaMapping
                    key={schemaMap.key}
                    type="button"
                    isSelected={isSelected}
                    onClick={() => handleSelectSchema(schemaMap.key)}
                  >
                    <span>{schemaMap.title}</span>
                    {isSelected && (
                      <CheckIconWrapper>
                        <CheckSmall />
                      </CheckIconWrapper>
                    )}
                  </SizeSchemaMapping>
                );
              })}
            </SizeSchemaMappingWrapper>
          </SizeSchemaDropdown>
        </SizeSchemaDropdownWrapper>
      </DropdownContainer>

      {product && notifyPopupSizeMapping && (
        <BackInStockNotification
          isOpen={!!notifyPopupSizeMapping}
          onClose={() => {
            setNotifyPopupSizeMapping(null);
            onBackInStockPopupChange?.(false);
          }}
          product={product}
          sizeMapping={notifyPopupSizeMapping}
          selectedSize={`${formatBrandSize(notifyPopupSizeMapping)}${
            formatTranslatedSize(notifyPopupSizeMapping, true)
              ? ` / ${formatTranslatedSize(notifyPopupSizeMapping, true)}`
              : ""
          }`}
          isMobile={false}
        />
      )}
    </>
  );
};

export default SizeSelectorPanel;
